import { EyeOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Form, Skeleton, Space, Typography } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"
import { useNavigate } from "react-router-dom"

import { FixedContainer } from "../../../components/FixedContainer.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useDateFormat } from "../../../hooks/use-date-format.ts"
import { showErrorNotifications } from "../../../utils/handlers/handle-error"
import { getProposalSyncStatus } from "../../DocumentSync/api/services.ts"
import { ProposalGenerateConfirmModal } from "../../DocumentSync/ProposalGenerateConfirmModal.tsx"
import type { FieldComparison } from "../../DocumentSync/types.ts"
import { contractValidationHasIssues } from "../ContractValidation/calculate-contract-validation.ts"
import { showDocumentValidationError } from "../helpers/show-document-validation-error.tsx"
import { getProposalValidation } from "../ProposalValidation/api/services.ts"
import { ProposalValidationCard } from "../ProposalValidation/ProposalValidationCard.tsx"
import { ProposalValidationConfirmModal } from "../ProposalValidation/ProposalValidationConfirmModal.tsx"
import type { ProposalValidationResult } from "../ProposalValidation/types.ts"
import {
  fetchProposalDocumentById,
  saveProposalDocument,
  updateProposalDocument
} from "../redux/thunks/document-proposal.thunks.ts"
import { fetchDocumentPreview } from "../redux/thunks/document-templates.thunks.ts"
import {
  type Document,
  type DocumentRequestData
} from "../types/document.types.ts"
import { type TemplateDocumentFormData } from "../types/template-document-form-data.tsx"
import { DocumentVersionHistoryList } from "../components/DocumentVersionHistoryList.tsx"
import { DocumentBackToLeadBtn } from "./components/DocumentBackToLeadBtn.tsx"
import { TemplateDocumentForm } from "./components/TemplateDocumentForm/TemplateDocumentForm.tsx"
import { TemplateDocumentPreview } from "./components/TemplateDocumentPreview.tsx"

const { Title } = Typography

function DocumentProposalEditPage({
  fetchDocumentById,
  updateDocument,
  saveDocument,
  fetchDocumentPreview
}: ConnectedProps<typeof connector>) {
  const navigate = useNavigate()
  const documentId = useParams<{ id: string }>()?.id as string
  const [form] = Form.useForm<TemplateDocumentFormData>()
  const { normalizeDate } = useDateFormat()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [document, setDocument] = useState<Document | null>(null)
  const [generateConfirmOpen, setGenerateConfirmOpen] = useState(false)
  const [generateConfirmDiffs, setGenerateConfirmDiffs] = useState<
    FieldComparison[]
  >([])
  const [generateConfirmDocumentLabel, setGenerateConfirmDocumentLabel] =
    useState<string | undefined>(undefined)
  const [validationConfirmOpen, setValidationConfirmOpen] = useState(false)
  const [validationResult, setValidationResult] =
    useState<ProposalValidationResult | null>(null)

  const leadId = document?.lead_id
  const alreadyHasFile = !!document?.attachments?.length

  const handleFetchDocumentPreview = useCallback(
    async (data: DocumentRequestData) => {
      return fetchDocumentPreview({ ...data, proposal_id: documentId }).unwrap()
    },
    [documentId, fetchDocumentPreview]
  )

  const handleUpdateDocument = useCallback(async () => {
    const data = await form.validateFields()
    return updateDocument({
      id: documentId,
      data: {
        ...data,
        completion_date: normalizeDate(data?.completion_date),
        start_date: normalizeDate(data?.start_date)
      }
    })?.unwrap()
  }, [documentId, form, updateDocument])

  const handlePreviewUpdateDocument = useCallback(async () => {
    try {
      setSaving(true)

      const res = await handleUpdateDocument()
      const updatedLeadId = res?.lead_id

      if (!updatedLeadId) throw new Error("Lead id is required")

      navigate(RoutesPaths.lead.view(updatedLeadId))
    } catch (e) {
      if (!showDocumentValidationError(form, e)) showErrorNotifications(e)
    } finally {
      setSaving(false)
    }
  }, [navigate, handleUpdateDocument])

  // The actual PDF-render/save-file step plus navigation — shared by the
  // no-diff path (called immediately) and both `ProposalGenerateConfirmModal`
  // resolutions ("Keep Lead as is" and "Update Lead", after its own sync
  // call resolves). Self-contained (own try/catch/saving) so it behaves the
  // same whether it's awaited inline or triggered later from the modal.
  const finishGenerate = useCallback(async () => {
    try {
      setSaving(true)
      if (!documentId) throw new Error("Document id is required")

      await saveDocument(documentId).unwrap()
      const leadId = document?.lead_id
      if (!leadId) throw new Error("Lead id is required")
      navigate(RoutesPaths.lead.view(leadId, { tab: "documents" }))
    } catch (e) {
      showErrorNotifications(e)
    } finally {
      setSaving(false)
    }
  }, [document?.lead_id, documentId, navigate, saveDocument])

  // The existing Lead sync-check step, unchanged — extracted so the new
  // Proposal Validation gate (below) can sit in FRONT of it without altering
  // anything about how it behaves once reached, whether that's immediately
  // (no validation warnings) or after "Generate Anyway". Mirrors
  // `DocumentContractEditPage.runSyncCheckAndFinish`.
  const runSyncCheckAndFinish = useCallback(async () => {
    if (!documentId) throw new Error("Document id is required")

    const statusRes = await getProposalSyncStatus(documentId)
    const diffs = statusRes.ok
      ? (statusRes.data?.fields ?? []).filter(
          (field) => field.has_diff && field.key !== "sales_owner"
        )
      : []

    if (!diffs.length) {
      await finishGenerate()
      return
    }

    setGenerateConfirmDiffs(diffs)
    setGenerateConfirmDocumentLabel(
      statusRes.ok ? statusRes.data?.document_label : undefined
    )
    setGenerateConfirmOpen(true)
  }, [documentId, finishGenerate])

  const handleSaveAndUpdate = useCallback(async () => {
    try {
      setSaving(true)
      await handleUpdateDocument()

      if (!documentId) throw new Error("Document id is required")

      // Authoritative recheck against what was just saved — purely an
      // informational gate in front of the existing sync-check/generate
      // flow below, never a replacement for it. A failed/unreachable check
      // must never block Generate, so it's swallowed rather than surfaced.
      let validation: ProposalValidationResult | null = null
      try {
        const validationRes = await getProposalValidation(documentId)
        validation = validationRes.ok ? (validationRes.data ?? null) : null
      } catch {
        validation = null
      }

      if (validation && contractValidationHasIssues(validation)) {
        setValidationResult(validation)
        setValidationConfirmOpen(true)
        return
      }

      await runSyncCheckAndFinish()
    } catch (e) {
      if (!showDocumentValidationError(form, e)) showErrorNotifications(e)
    } finally {
      setSaving(false)
    }
  }, [documentId, form, handleUpdateDocument, runSyncCheckAndFinish])

  const handleValidationConfirmProceed = useCallback(async () => {
    try {
      setSaving(true)
      await runSyncCheckAndFinish()
    } catch (e) {
      showErrorNotifications(e)
    } finally {
      setSaving(false)
    }
  }, [runSyncCheckAndFinish])

  const handleGetDocument = useCallback(async () => {
    if (!documentId) return

    try {
      setLoading(true)
      const document = await fetchDocumentById(documentId)?.unwrap()
      if (document) setDocument(document)
    } finally {
      setLoading(false)
    }
  }, [documentId, fetchDocumentById])

  useEffect(() => {
    void handleGetDocument()
    generateDocumentTitle("Edit Document")
  }, [handleGetDocument])

  if (loading) return <Skeleton.Node active className={"size-full"} />
  if (!loading && !document) return <div>Document not found</div>

  // Every "Edit"/"View" in the version history jumps to that version's own
  // edit route — the destination page itself already decides editable vs.
  // read-only (the `is_editable` check right below), so both actions can
  // point at the same URL and let the destination sort it out.
  const goToVersion = (id: string) =>
    navigate(RoutesPaths.proposalDocument.edit(id))

  // Defense-in-depth: the row-action list already hides Edit for a
  // superseded version, but a stale tab or a bookmarked/typed edit URL
  // reaches this page directly. Block the editable form client-side rather
  // than let the user fill it out and discover only at Save that the
  // backend's PATCH guard (400/403) rejects it — `is_editable` already
  // folds in "is this the latest version" (backend), so this one check
  // covers both a superseded version and any other PATCH-blocking reason.
  if (document?.is_editable === false) {
    return (
      <FixedContainer>
        <Flex
          vertical
          gap={"middle"}
          align={"center"}
          justify={"center"}
          className={"size-full"}
        >
          <Alert
            data-testid={"historical-version-blocked"}
            type={"info"}
            showIcon
            message={"This is a historical version"}
            description={
              "A newer version of this Proposal exists, so this one is read-only. Open the latest version from the Lead to make changes."
            }
          />
          {document?.version_history && document.version_history.length > 1 && (
            <div className={"w-full max-w-md"}>
              <DocumentVersionHistoryList
                versionHistory={document.version_history}
                onEditVersion={goToVersion}
                onViewVersion={goToVersion}
              />
            </div>
          )}
          {leadId && <DocumentBackToLeadBtn leadId={leadId} saving={false} />}
        </Flex>
      </FixedContainer>
    )
  }

  return (
    <FixedContainer>
      <Flex vertical gap={"small"} className={"overflow-hidden"}>
        <Flex justify={"space-between"} align={"center"} wrap gap={"small"}>
          <Flex align={"center"} gap={"small"}>
            <Title level={4} style={{ margin: 0 }}>
              Edit Document
            </Title>
            {/*<Text type="secondary">Template: {template?.name}</Text>*/}
          </Flex>
          <Space wrap>
            {leadId && (
              <DocumentBackToLeadBtn leadId={leadId} saving={saving} />
            )}
            <TemplateDocumentPreview
              saving={saving}
              form={form}
              leadId={leadId}
              customerId={document?.customer_id}
              alreadyHasFile={alreadyHasFile}
              fetchDocumentPreview={handleFetchDocumentPreview}
              onSubmitClick={handlePreviewUpdateDocument}
              onGenerateClick={handleSaveAndUpdate}
            />

            <Button
              loading={saving}
              onClick={form.submit}
              variant={"solid"}
              color={"primary"}
              icon={<EyeOutlined />}
            >
              {alreadyHasFile ? "Update & Regenerate" : "Update & Generate"}
            </Button>
          </Space>
        </Flex>

        <ProposalValidationConfirmModal
          open={validationConfirmOpen}
          onClose={() => setValidationConfirmOpen(false)}
          result={validationResult}
          onProceed={handleValidationConfirmProceed}
        />

        <ProposalGenerateConfirmModal
          open={generateConfirmOpen}
          onClose={() => setGenerateConfirmOpen(false)}
          diffs={generateConfirmDiffs}
          proposalId={documentId}
          documentLabel={generateConfirmDocumentLabel}
          onProceed={finishGenerate}
        />

        {document?.version_history && document.version_history.length > 1 && (
          <DocumentVersionHistoryList
            versionHistory={document.version_history}
            onEditVersion={goToVersion}
            onViewVersion={goToVersion}
          />
        )}

        <ProposalValidationCard form={form} className={"mb-3"} />

        <TemplateDocumentForm
          form={form}
          saving={saving}
          projectTypes={document?.project_type_details || []}
          initialValues={document || undefined}
          leadId={leadId}
          onSubmit={handleSaveAndUpdate}
        />
      </Flex>
    </FixedContainer>
  )
}

const mapStateToProps = () => ({})

const mapDispatchToProps = {
  fetchDocumentById: fetchProposalDocumentById,
  updateDocument: updateProposalDocument,
  saveDocument: saveProposalDocument,
  fetchDocumentPreview
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(DocumentProposalEditPage)
